import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';

// MUI Components
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';

// MUI Styles
import { makeStyles } from '@mui/styles';

// Custom Components
import GPAP_Tooltip from "../../../../../../gpap-lib/components/GPAP_Tooltip";
import { GeneTableRow } from './GeneTableRow';
import { connect, useSelector } from "react-redux";
const CNV_AGGREGATION_URL = () =>
    (typeof window !== 'undefined' && window.config?.apis_configuration?.api_nextgpap_server_elastic)
        ? window.config.apis_configuration.api_nextgpap_server_elastic + "/api/cnvs"
        : '';

const _cnvAggFetchedCache = new Set();

/**
 * Recursively collect unique Experiment_ID values from aggregation groups.
 * @param {Object} node - A node in the aggregation tree (may have groups or Experiment_ID)
 * @param {Set<string>} out - Set to which experiment IDs are added
 */
function collectExperimentIds(node, out) {
    if (!node) return;
    if (node.hasOwnProperty('Experiment_ID') && node.Experiment_ID != null) {
        out.add(String(node.Experiment_ID));
        return;
    }
    if (Array.isArray(node.groups)) {
        node.groups.forEach((child) => collectExperimentIds(child, out));
    }
}

/**
 * Parse CNV aggregation response into a map: geneName -> { count, experimentIds }.
 * @param {Array} aggregations - response.aggregations
 * @returns {Map<string, { count: number, experimentIds: Set<string> }>}
 */
function parseCnvAggregations(aggregations) {
    const map = new Map();
    if (!Array.isArray(aggregations)) return map;
    aggregations.forEach((item) => {
        const gene = item.Gene_name != null ? String(item.Gene_name) : null;
        if (gene == null) return;
        const count = typeof item.count === 'number' ? item.count : 0;
        const experimentIds = new Set();
        collectExperimentIds(item, experimentIds);
        map.set(gene, { count, experimentIds });
    });
    return map;
}

/** Build summary-shaped array from CNV aggregation map (for Genomics runSearchAcrossQuery when only CNV results exist). */
function cnvMapToSummaryArray(cnvByGeneMap) {
    if (!cnvByGeneMap || cnvByGeneMap.size === 0) return [];
    return Array.from(cnvByGeneMap.entries()).map(([gene, { count, experimentIds }]) => ({
        gene,
        n_var: 0,
        n_cnv: count,
        n_exp: experimentIds.size,
        sample_list: [...experimentIds]
    }));
}

/** Collect all distinct Experiment_IDs from CNV aggregation map for participant fetch. */
function getDistinctExperimentIdsFromCnvMap(cnvByGeneMap) {
    if (!cnvByGeneMap || cnvByGeneMap.size === 0) return [];
    const out = new Set();
    cnvByGeneMap.forEach(({ experimentIds }) => {
        if (experimentIds && typeof experimentIds.forEach === "function") experimentIds.forEach((id) => out.add(id));
    });
    return [...out];
}

/**
 * Filter CNV aggregation map to only include genes that were in the request body (client-side workaround for server returning extra genes).
 * If geneList is null/undefined or empty, returns the map unchanged.
 */
function filterCnvMapByGeneList(cnvByGeneMap, geneList) {
    if (!cnvByGeneMap || cnvByGeneMap.size === 0) return cnvByGeneMap;
    if (!Array.isArray(geneList) || geneList.length === 0) return cnvByGeneMap;
    const allowed = new Set(geneList.map((g) => String(g).trim()));
    const filtered = new Map();
    cnvByGeneMap.forEach((value, gene) => {
        if (allowed.has(String(gene).trim())) filtered.set(gene, value);
    });
    return filtered;
}

/** Filter raw aggregations array by gene list for ParticipantTable (CNV-only cohort). */
function filterRawAggregationsByGeneList(aggregations, geneList) {
    if (!Array.isArray(aggregations)) return [];
    if (!Array.isArray(geneList) || geneList.length === 0) return aggregations;
    const allowed = new Set(geneList.map((g) => String(g).trim()));
    return aggregations.filter((a) => a && allowed.has(String(a.Gene_name).trim()));
}

/** Detail text for Snackbar (parent adds the "CNV query: " prefix). */
function messageFromCnvAggregationError(err) {
    const status = err.response?.status;
    const data = err.response?.data;
    let detail = "";
    if (data == null) {
        detail = err.message || "";
    } else if (typeof data === "string") {
        detail = data;
    } else if (data && typeof data.message === "string") {
        detail = data.message;
    } else if (data && typeof data.error === "string") {
        detail = data.error;
    }
    detail = (detail && String(detail).trim()) || "The request failed.";
    if (status) {
        return `HTTP ${status} — ${detail}`;
    }
    return detail;
}

const useStyles = makeStyles({
    table: {
        minWidth: 0,
    },
    selectedRow: {
        backgroundColor: '#cfe2f3', // Light blue background for selected row
        border: '2px solid #4f8abf', // Darker blue border
        '&:hover': {
            backgroundColor: '#a9c7e7', // Slightly darker on hover for visual feedback
            cursor: 'pointer', // Show pointer cursor on hover
        },
    },
    geneNameSelected: {
        color: '#4f8abf',  // Apply the desired color to the gene name when selected
        fontWeight: 500,    // Apply the desired font weight
    },
    pointerCursorCell: {
        cursor: 'pointer', // Apply pointer cursor to all table cells
    },
});

/** Experiment IDs for CNV aggregation — always the full query scope, not gene-narrowed participants.experiments. */
function resolveAggregationExperiments(props) {
    if (Array.isArray(props.cohortExperimentIds) && props.cohortExperimentIds.length > 0) {
        return props.cohortExperimentIds;
    }
    if (Array.isArray(props.fullExperimentIds) && props.fullExperimentIds.length > 0) {
        return props.fullExperimentIds;
    }
    return props.data?.experiments;
}

function dynamicSort(property) {
    let sortOrder = 1;

    if(property[0] === "-") {
        sortOrder = -1;
        property = property.substr(1);
    }

    return function (a, b) {
        if(sortOrder === -1){
            return b[property].localeCompare(a[property]);
        } else {
            return a[property].localeCompare(b[property]);
        }
    }
}

function GeneAggTable(props) {
    const classes = useStyles();
    const [row_selected, setSelected] = useState("All");
    const [allRow, setAllRow] = useState(null);
    const [queryID, setQueryID] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cnvByGene, setCnvByGene] = useState(new Map());
    const [cnvAggregationLoading, setCnvAggregationLoading] = useState(false);
    const lastAggregationKeyRef = useRef(null);
    const aggregationInFlightRef = useRef(false);

    const freqInt = useSelector(state => state.query.freqInt);
    const clinvar = useSelector(state => state.query.clinvarclasses);
    const acmg = useSelector(state => state.query.intervarclasses);

    useEffect(() => {
        if (props.data && props.data.hasOwnProperty("total")) {
            if (props.totalVariants !== props.data.total) {
                // setSelected(-1);
            }
        }
    }, [props.totalVariants]);

    // When a new query is run, reset gene selection and clear the fetch cache so fresh aggregation runs with the new gene list.
    useEffect(() => {
        if (props.query_id && props.query_id !== queryID) {
            setQueryID(props.query_id);
            setSelected("All");
            setCnvByGene(new Map());
            props.onCnvAggregationError?.(null);
            lastAggregationKeyRef.current = null;
            aggregationInFlightRef.current = false;
            _cnvAggFetchedCache.clear();
        }
    }, [props.query_id]);

    // Stable serialized key — only triggers when query_id, experiments, or rows change.
    // geneList is intentionally excluded: it updates live from Redux as the user edits filters,
    // and should NOT trigger a new aggregation before "Run query" is pressed.
    // The gene list is still read inside the effect for the request body and dedup key.
    const _cohortIdsKey = JSON.stringify(props.cohortExperimentIds || []);
    const _fullExpsKey = JSON.stringify(props.fullExperimentIds || []);
    const _dataExpsKey = JSON.stringify(props.data?.experiments || []);
    const _aggDepsKey = useMemo(() => {
        const exps = resolveAggregationExperiments(props);
        return JSON.stringify({
            q: props.query_id,
            e: Array.isArray(exps) ? [...exps].sort() : [],
            r: Array.isArray(props.rows) ? props.rows.length : 0
        });
    }, [props.query_id, _cohortIdsKey, _fullExpsKey, _dataExpsKey, props.rows?.length]);

    useEffect(() => {
        const experiments = resolveAggregationExperiments(props);
        const isAllSelected = row_selected === "All" || row_selected == null;
        if (!isAllSelected) {
            lastAggregationKeyRef.current = null;
            aggregationInFlightRef.current = false;
            return;
        }
        const hasExperiments = Array.isArray(experiments) && experiments.length > 0;
        const hasRows = Array.isArray(props.rows) && props.rows.length > 0;
        if (!props.query_id || !hasExperiments) {
            setCnvByGene(new Map());
            return;
        }
        if (!hasRows && !(Array.isArray(props.cohortExperimentIds) && props.cohortExperimentIds.length > 0)) {
            setCnvByGene(new Map());
            return;
        }
        const expIdsSorted = [...experiments].sort();
        const geneListSorted = Array.isArray(props.geneList) ? [...props.geneList].sort() : [];
        const aggregationKey = JSON.stringify({
            query_id: props.query_id,
            expIds: expIdsSorted,
            geneList: geneListSorted
        });
        if (_cnvAggFetchedCache.has(aggregationKey)) {
            props.onCnvAggregationError?.(null);
            props.onCnvAggregationComplete?.(false);
            return;
        }
        if (lastAggregationKeyRef.current === aggregationKey) {
            return;
        }
        if (aggregationInFlightRef.current) {
            return;
        }
        lastAggregationKeyRef.current = aggregationKey;
        aggregationInFlightRef.current = true;
        setCnvAggregationLoading(true);
        props.onCnvAggregationError?.(null);

        const url = CNV_AGGREGATION_URL();
        if (!url || !props.token) {
            setCnvByGene(new Map());
            setCnvAggregationLoading(false);
            aggregationInFlightRef.current = false;
            return;
        }
        const body = {
            page: 1,
            pageSize: 10000,
            fields: [],
            sorted: [],
            filtered: [{ id: "Experiment_ID", value: experiments }, {id: "Annotation_mode", value: ["full"]}],
            aggregation: {
                fields: ["Gene_name", "SV_chrom", "SV_start", "SV_end", "Experiment_ID"]
            }
        };
        if (Array.isArray(props.geneList) && props.geneList.length > 0) {
            body.filtered.push({ id: "Gene_name", value: props.geneList });
        }

        if (freqInt !== null && freqInt !== undefined && freqInt !== "" && !isNaN(parseFloat(freqInt)) && isFinite(freqInt)) {
            body.filtered.push({ id: "Internal_Freq", value: [parseFloat(freqInt)], operator: "lte" });
        }

        if (clinvar !== null && clinvar !== undefined && typeof clinvar === 'object') {
            const clinvarMapping = { "P": "P", "L": "LP", "U": "VUS", "C": "C", "D": "Drug" };
            const mappedOpts = ["P", "LP", "VUS", "C", "Drug"];
            const allOpts = ["P", "LP", "VUS", "C", "LB", "B", "Drug", "Other"];
            const anyOpts = allOpts.filter(o => !mappedOpts.includes(o));
            const selected = Object.keys(clinvar)
                .filter(k => clinvar[k] === true)
                .flatMap(k => k === "A" ? anyOpts : (clinvarMapping[k] || k));
            const unique = [...new Set(selected)];
            if (unique.length > 0) {
                body.filtered.push({ id: "clinvar_clnsig", value: unique });
            }
        }

        if (acmg !== null && acmg !== undefined && typeof acmg === 'object') {
            const acmgMapping = { "B": "Benign", "LB": "Likely benign", "P": "Pathogenic", "LP": "Likely pathogenic", "VUS": "Uncertain significance" };
            const selected = Object.keys(acmg)
                .filter(k => acmg[k] === true)
                .map(k => acmgMapping[k] || k);
            const unique = [...new Set(selected)];
            if (unique.length > 0) {
                body.filtered.push({ id: "ACMG_class", value: unique });
            }
        }
        axios.post(url, body, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": props.token
            }
        })
            .then((res) => {
                const aggs = res.data?.aggregations;
                const map = filterCnvMapByGeneList(parseCnvAggregations(aggs), props.geneList);
                setCnvByGene(map);
                props.onCnvAggregationError?.(null);
                const hasResults = map.size > 0;
                if (!hasResults) {
                    _cnvAggFetchedCache.add(aggregationKey);
                }
                props.onCnvAggregationComplete?.(hasResults);
                props.onCnvAggregationData?.(filterRawAggregationsByGeneList(aggs || [], props.geneList));
                if ((!Array.isArray(props.rows) || props.rows.length === 0) && map.size > 0) {
                    props.onCnvSummaryReady?.(cnvMapToSummaryArray(map));
                }
                if ((!Array.isArray(props.rows) || props.rows.length === 0) && map.size > 0) {
                    props.onCnvExperimentsForParticipants?.(getDistinctExperimentIdsFromCnvMap(map));
                }
            })
            .catch((err) => {
                setCnvByGene(new Map());
                const msg = messageFromCnvAggregationError(err);
                props.onCnvAggregationError?.(msg);
                _cnvAggFetchedCache.add(aggregationKey);
                props.onCnvAggregationComplete?.(false);
                props.onCnvAggregationData?.([]);
            })
            .finally(() => {
                setCnvAggregationLoading(false);
                aggregationInFlightRef.current = false;
            });
    }, [row_selected, props.token, _aggDepsKey]);

    const setAllRowInfo = (rows) => {
        if (!rows || rows.length === 0) return;
        let sum_variants = 0;
        let sum_cnv = 0;
        const allSamples = new Set();
        rows.forEach((item) => {
            sum_variants += parseInt(item.n_var, 10) || 0;
            sum_cnv += parseInt(item.n_cnv, 10) || 0;
            (item.sample_list || []).forEach((s) => allSamples.add(s));
        });
        setAllRow({
            gene: "All",
            n_var: sum_variants,
            n_cnv: sum_cnv,
            n_exp: allSamples.size
        });
    };

    const mergedRows = useMemo(() => {
        const rows = Array.isArray(props.rows) ? props.rows : [];
        const result = [];
        const genesInResult = new Set();

        // Add rows for each SNV gene, enriched with CNV data when present
        rows.forEach((row) => {
            genesInResult.add(row.gene);
            const cnv = cnvByGene.get(row.gene);
            const cnvCount = cnv ? cnv.count : 0;
            const cnvExpIds = cnv ? cnv.experimentIds : new Set();
            const snvSamples = row.sample_list || [];
            const mergedExpSet = new Set([...snvSamples, ...cnvExpIds]);
            result.push({
                ...row,
                n_cnv: cnvCount,
                n_exp: mergedExpSet.size,
                sample_list: [...mergedExpSet]
            });
        });

        // Add rows for genes that have only CNV (no SNV) so both appear in the table
        cnvByGene.forEach(({ count, experimentIds }, gene) => {
            if (genesInResult.has(gene)) return;
            genesInResult.add(gene);
            result.push({
                gene,
                n_var: 0,
                n_cnv: count,
                n_exp: experimentIds.size,
                sample_list: [...experimentIds]
            });
        });

        return result;
    }, [props.rows, cnvByGene]);

    useEffect(() => {
        if (props.query_id !== queryID) {
            setIsLoading(true);
            setAllRow(null);
            const timer = setTimeout(() => {
                setQueryID(props.query_id);
                setIsLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [props.query_id, props.rows, props.n_experiments]);

    useEffect(() => {
        if (queryID != null && props.query_id === queryID && mergedRows.length > 0) {
            setAllRowInfo(mergedRows);
        }
    }, [mergedRows, queryID, props.query_id]);

    const handleClick = (event, row_name) => {
        const sel_index = row_selected !== row_name;
        let newSelected = -1;
        if (sel_index) {
            newSelected = row_name;
            props.runSearchAcrossQuery(row_name);
        } else {
            newSelected = -1;
        }

        setSelected(newSelected);
    };

    const isSelected = (name) => row_selected === name;

    const sortedMergedRows = useMemo(() => {
        const copy = [...mergedRows];
        copy.sort(dynamicSort("gene"));
        return copy;
    }, [mergedRows]);

    const createAllGenesRow = () => {
        if (!allRow) return null;
        return (
            <GeneTableRow
                isItemSelected={isSelected("All")}
                row={allRow}
                classes={classes}
                handleClick={handleClick}
            />
        );
    };

    const renderTable = () => {
        if (isLoading && !allRow) {
            return (
                <div>
                    <CircularProgress />
                </div>
            );
        }
        // Cohort with no SNV rows: show loading while CNV aggregation is in progress
        const cohortOnlyMode = Array.isArray(props.cohortExperimentIds) && props.cohortExperimentIds.length > 0 && (!Array.isArray(props.rows) || props.rows.length === 0);
        if (cohortOnlyMode && cnvAggregationLoading && !allRow) {
            return (
                <div>
                    <CircularProgress />
                </div>
            );
        }
        if (!allRow) {
            return null;
        }

        return (
            <TableContainer component={Paper} style={{ maxHeight: "600px" }}>
                <Table className={classes.table} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Gene</TableCell>
                            <GPAP_Tooltip title={"# Number of SNV/INDEL variants in this gene"}>
                                <TableCell className={classes.pointerCursorCell} align="left"># SNV/InDels</TableCell>
                            </GPAP_Tooltip>
                            <GPAP_Tooltip title={"Number of CNV/SV variants in this gene"}>
                                <TableCell className={classes.pointerCursorCell} align="left"># CNV/SV</TableCell>
                            </GPAP_Tooltip>
                            <GPAP_Tooltip title={"# Experiments found with variants (SNV/INDEL or CNV/SV) in this specific gene"}>
                                <TableCell className={classes.pointerCursorCell} align="left"># Exp.</TableCell>
                            </GPAP_Tooltip>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {createAllGenesRow()}
                        {sortedMergedRows.map((row) => (
                            <GeneTableRow
                                key={row.gene}
                                isItemSelected={isSelected(row.gene)}
                                row={row}
                                classes={classes}
                                handleClick={handleClick}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    
    return (renderTable());
}

function mapStateToProps (state) {
    if(state.authorization!==undefined)
    {
        return {
            query_id: state.studySettings.current_query,
            geneList: state.query?.gene || []
        };
    }
}


export default connect(mapStateToProps)(GeneAggTable);